// src/components/workloads/creation/CensusBlockSelector.tsx
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Grid3X3, Search, Loader2, AlertTriangle, MapPin, CheckCircle, Download } from 'lucide-react';
import { useWorkloadStore } from '../../../store/workloadStore';
import GoogleMapProvider from '../../map/GoogleMapProvider';
import BaseMap from '../../map/BaseMap';
import { addressDatabaseService } from '../../../services/addressDatabase.service';
import { exportCreationAddressesToCSV } from '../../../utils/addressExport';

// State FIPS codes for Census API
const US_STATES = [
  { code: '01', abbr: 'AL', name: 'Alabama' },
  { code: '02', abbr: 'AK', name: 'Alaska' },
  { code: '04', abbr: 'AZ', name: 'Arizona' },
  { code: '05', abbr: 'AR', name: 'Arkansas' },
  { code: '06', abbr: 'CA', name: 'California' },
  { code: '08', abbr: 'CO', name: 'Colorado' },
  { code: '09', abbr: 'CT', name: 'Connecticut' },
  { code: '10', abbr: 'DE', name: 'Delaware' },
  { code: '11', abbr: 'DC', name: 'District of Columbia' },
  { code: '12', abbr: 'FL', name: 'Florida' },
  { code: '13', abbr: 'GA', name: 'Georgia' },
  { code: '15', abbr: 'HI', name: 'Hawaii' },
  { code: '16', abbr: 'ID', name: 'Idaho' },
  { code: '17', abbr: 'IL', name: 'Illinois' },
  { code: '18', abbr: 'IN', name: 'Indiana' },
  { code: '19', abbr: 'IA', name: 'Iowa' },
  { code: '20', abbr: 'KS', name: 'Kansas' },
  { code: '21', abbr: 'KY', name: 'Kentucky' },
  { code: '22', abbr: 'LA', name: 'Louisiana' },
  { code: '23', abbr: 'ME', name: 'Maine' },
  { code: '24', abbr: 'MD', name: 'Maryland' },
  { code: '25', abbr: 'MA', name: 'Massachusetts' },
  { code: '26', abbr: 'MI', name: 'Michigan' },
  { code: '27', abbr: 'MN', name: 'Minnesota' },
  { code: '28', abbr: 'MS', name: 'Mississippi' },
  { code: '29', abbr: 'MO', name: 'Missouri' },
  { code: '30', abbr: 'MT', name: 'Montana' },
  { code: '31', abbr: 'NE', name: 'Nebraska' },
  { code: '32', abbr: 'NV', name: 'Nevada' },
  { code: '33', abbr: 'NH', name: 'New Hampshire' },
  { code: '34', abbr: 'NJ', name: 'New Jersey' },
  { code: '35', abbr: 'NM', name: 'New Mexico' },
  { code: '36', abbr: 'NY', name: 'New York' },
  { code: '37', abbr: 'NC', name: 'North Carolina' },
  { code: '38', abbr: 'ND', name: 'North Dakota' },
  { code: '39', abbr: 'OH', name: 'Ohio' },
  { code: '40', abbr: 'OK', name: 'Oklahoma' },
  { code: '41', abbr: 'OR', name: 'Oregon' },
  { code: '42', abbr: 'PA', name: 'Pennsylvania' },
  { code: '44', abbr: 'RI', name: 'Rhode Island' },
  { code: '45', abbr: 'SC', name: 'South Carolina' },
  { code: '46', abbr: 'SD', name: 'South Dakota' },
  { code: '47', abbr: 'TN', name: 'Tennessee' },
  { code: '48', abbr: 'TX', name: 'Texas' },
  { code: '49', abbr: 'UT', name: 'Utah' },
  { code: '50', abbr: 'VT', name: 'Vermont' },
  { code: '51', abbr: 'VA', name: 'Virginia' },
  { code: '53', abbr: 'WA', name: 'Washington' },
  { code: '54', abbr: 'WV', name: 'West Virginia' },
  { code: '55', abbr: 'WI', name: 'Wisconsin' },
  { code: '56', abbr: 'WY', name: 'Wyoming' },
];

interface County {
  fipsCode: string;
  name: string;
}

interface CensusBlockGroup {
  id: string;
  name: string;
  tractCode: string;
  blockGroupCode: string;
  population: number;
  bounds?: google.maps.LatLngBoundsLiteral;
  polygon?: Array<{ lat: number; lng: number }>;
}

const CensusBlockSelector: React.FC = () => {
  const { creation, setCreationAddresses } = useWorkloadStore();
  const mapRef = useRef<google.maps.Map | null>(null);
  const polygonsRef = useRef<google.maps.Polygon[]>([]);

  const [selectedState, setSelectedState] = useState<string>('');
  const [selectedStateAbbr, setSelectedStateAbbr] = useState<string>('');
  const [selectedCounty, setSelectedCounty] = useState<string>('');
  const [counties, setCounties] = useState<County[]>([]);
  const [censusBlocks, setCensusBlocks] = useState<CensusBlockGroup[]>([]);
  const [selectedBlocks, setSelectedBlocks] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({ lat: 39.8283, lng: -98.5795 }); // US center
  const [mapZoom, setMapZoom] = useState<number>(4);
  // Track when the Google map instance is actually ready. Without this the
  // polygon-draw useEffect could fire before mapRef.current was set, leaving
  // boundaries in memory but never on the map. Surfaces as "I see nothing".
  const [mapLoaded, setMapLoaded] = useState(false);
  const [boundariesError, setBoundariesError] = useState<string | null>(null);

  // Fetch counties from Census Bureau API when state changes
  const handleStateChange = useCallback(async (stateFips: string) => {
    setSelectedState(stateFips);
    setSelectedCounty('');
    setCensusBlocks([]);
    setSelectedBlocks([]);
    setCounties([]);
    setError(null);

    // Find state abbreviation
    const stateInfo = US_STATES.find(s => s.code === stateFips);
    setSelectedStateAbbr(stateInfo?.abbr || '');

    if (!stateFips) {
      return;
    }

    setIsLoading(true);
    setLoadingMessage('Fetching counties from Census Bureau...');

    try {
      // Use Census Bureau API to get counties
      const response = await fetch(
        `https://api.census.gov/data/2020/dec/pl?get=NAME&for=county:*&in=state:${stateFips}`
      );

      if (!response.ok) {
        throw new Error(`Census API error: ${response.status}`);
      }

      const data = await response.json();

      // Data format: [["NAME","state","county"], ["Autauga County","01","001"], ...]
      // Skip header row
      const countyList: County[] = data.slice(1).map((row: string[]) => ({
        name: row[0].replace(' County', '').replace(' Parish', '').replace(' Borough', ''),
        fipsCode: row[2]
      })).sort((a: County, b: County) => a.name.localeCompare(b.name));

      setCounties(countyList);

      // Get state centroid for map
      const stateCentroid = await getStateCentroid(stateFips);
      if (stateCentroid) {
        setMapCenter(stateCentroid);
        setMapZoom(7);
      }
    } catch (err: any) {
      console.error('Error fetching counties:', err);
      setError(`Failed to fetch counties: ${err.message}`);
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  }, []);

  // Fetch census block groups when county changes
  const handleCountyChange = useCallback(async (countyFips: string) => {
    setSelectedCounty(countyFips);
    setCensusBlocks([]);
    setSelectedBlocks([]);
    setError(null);

    // Clear existing polygons
    polygonsRef.current.forEach(p => p.setMap(null));
    polygonsRef.current = [];

    if (!countyFips || !selectedState) return;

    setIsLoading(true);
    setLoadingMessage('Fetching census block groups...');

    try {
      // Fetch block groups with population data
      const response = await fetch(
        `https://api.census.gov/data/2020/dec/pl?get=NAME,P1_001N&for=block%20group:*&in=state:${selectedState}&in=county:${countyFips}&in=tract:*`
      );

      if (!response.ok) {
        throw new Error(`Census API error: ${response.status}`);
      }

      const data = await response.json();

      // Data format: [["NAME","P1_001N","state","county","tract","block group"], ...]
      const blockGroups: CensusBlockGroup[] = data.slice(1).map((row: string[]) => {
        const tractCode = row[4];
        const blockGroupCode = row[5];
        return {
          id: `${selectedState}${countyFips}${tractCode}${blockGroupCode}`,
          name: row[0],
          tractCode,
          blockGroupCode,
          population: parseInt(row[1], 10) || 0,
        };
      }).filter((bg: CensusBlockGroup) => bg.population > 0) // Only blocks with population
        .sort((a: CensusBlockGroup, b: CensusBlockGroup) => b.population - a.population); // Sort by population

      setCensusBlocks(blockGroups);

      // Get county centroid for map
      const countyCentroid = await getCountyCentroid(selectedState, countyFips);
      if (countyCentroid) {
        setMapCenter(countyCentroid);
        setMapZoom(11);
      }

      // Fetch boundaries for block groups
      if (blockGroups.length > 0) {
        setLoadingMessage('Loading block group boundaries...');
        await fetchBlockGroupBoundaries(selectedState, countyFips, blockGroups);
      }
    } catch (err: any) {
      console.error('Error fetching block groups:', err);
      setError(`Failed to fetch block groups: ${err.message}`);
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  }, [selectedState]);

  // Get state centroid using geocoding
  const getStateCentroid = async (stateFips: string): Promise<{ lat: number; lng: number } | null> => {
    const stateInfo = US_STATES.find(s => s.code === stateFips);
    if (!stateInfo || !window.google?.maps) return null;

    return new Promise((resolve) => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: `${stateInfo.name}, USA` }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          const loc = results[0].geometry.location;
          resolve({ lat: loc.lat(), lng: loc.lng() });
        } else {
          resolve(null);
        }
      });
    });
  };

  // Get county centroid using geocoding
  const getCountyCentroid = async (stateFips: string, countyFips: string): Promise<{ lat: number; lng: number } | null> => {
    const stateInfo = US_STATES.find(s => s.code === stateFips);
    const county = counties.find(c => c.fipsCode === countyFips);
    if (!stateInfo || !county || !window.google?.maps) return null;

    return new Promise((resolve) => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: `${county.name} County, ${stateInfo.name}, USA` }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          const loc = results[0].geometry.location;
          resolve({ lat: loc.lat(), lng: loc.lng() });
        } else {
          resolve(null);
        }
      });
    });
  };

  // Fetch block group boundaries from TIGERweb
  const fetchBlockGroupBoundaries = async (
    stateFips: string,
    countyFips: string,
    blockGroups: CensusBlockGroup[]
  ) => {
    setBoundariesError(null);
    try {
      // Use TIGERweb ArcGIS REST API to get block group boundaries.
      // Esri JSON shape: { features: [{ attributes: {...}, geometry: { rings: [[[lng,lat], ...]] } }] }
      const whereClause = `STATE='${stateFips}' AND COUNTY='${countyFips}'`;
      const url = `https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/tigerWMS_Census2020/MapServer/10/query?` +
        `where=${encodeURIComponent(whereClause)}` +
        `&outFields=GEOID,BLKGRP,TRACT,ALAND` +
        `&returnGeometry=true` +
        `&outSR=4326` +
        `&f=json`;

      console.log('[CensusBlocks] Fetching boundaries:', url);
      const response = await fetch(url);
      if (!response.ok) {
        const msg = `TIGERweb returned ${response.status}`;
        console.warn(msg);
        setBoundariesError(`${msg} — block boundaries not available. Address fetch will use bounding boxes instead.`);
        return;
      }

      const data = await response.json();
      console.log('[CensusBlocks] TIGERweb returned', data?.features?.length || 0, 'features');

      if (!data.features || data.features.length === 0) {
        setBoundariesError('TIGERweb returned no boundary features for this county. Try a different county.');
        return;
      }

      // Update block groups with boundary data.
      const updatedBlocks = [...blockGroups];
      let matchedCount = 0;

      for (const feature of data.features) {
        const geoid = feature.attributes?.GEOID;
        const blockIndex = updatedBlocks.findIndex((bg) => bg.id === geoid);

        if (blockIndex >= 0 && feature.geometry?.rings?.[0]) {
          // Esri rings are [lng, lat] pairs. Convert to {lat, lng} literals.
          const ring = feature.geometry.rings[0];
          const polygon = ring.map((coord: number[]) => ({
            lat: coord[1],
            lng: coord[0],
          }));

          // Calculate per-block bounds.
          let north = -Infinity, south = Infinity, east = -Infinity, west = Infinity;
          for (const point of polygon) {
            north = Math.max(north, point.lat);
            south = Math.min(south, point.lat);
            east = Math.max(east, point.lng);
            west = Math.min(west, point.lng);
          }

          updatedBlocks[blockIndex] = {
            ...updatedBlocks[blockIndex],
            polygon,
            bounds: { north, south, east, west },
          };
          matchedCount++;
        }
      }

      console.log('[CensusBlocks] Matched', matchedCount, 'of', updatedBlocks.length, 'blocks with boundaries');
      setCensusBlocks(updatedBlocks);
    } catch (err: any) {
      const msg = `Failed to fetch block boundaries: ${err.message || err}`;
      console.warn(msg);
      setBoundariesError(msg);
    }
  };

  // Draw polygons on map for selected blocks. Depends on mapLoaded so the
  // effect re-runs once the Google map instance is actually ready — without
  // that, mapRef.current could be null on first render and polygons would
  // silently fail to attach.
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;

    // Clear existing polygons.
    polygonsRef.current.forEach((p) => p.setMap(null));
    polygonsRef.current = [];

    const blocksWithPolygon = censusBlocks.filter((b) => b.polygon && b.polygon.length > 0);
    if (blocksWithPolygon.length === 0) return;

    // Draw new polygons.
    for (const block of blocksWithPolygon) {
      const isSelected = selectedBlocks.includes(block.id);
      const polygon = new google.maps.Polygon({
        paths: block.polygon!,
        strokeColor: isSelected ? '#4F46E5' : '#4F46E5',
        strokeOpacity: isSelected ? 1 : 0.7,
        strokeWeight: isSelected ? 3 : 1.5,
        fillColor: isSelected ? '#4F46E5' : '#818CF8',
        fillOpacity: isSelected ? 0.45 : 0.2,
        map: mapRef.current,
        clickable: true,
        zIndex: isSelected ? 2 : 1,
      });

      polygon.addListener('click', () => {
        toggleBlock(block.id);
      });

      // Hover affordance: bump opacity on mouseover so users feel the cells.
      polygon.addListener('mouseover', () => {
        polygon.setOptions({ fillOpacity: isSelected ? 0.6 : 0.35 });
      });
      polygon.addListener('mouseout', () => {
        polygon.setOptions({ fillOpacity: isSelected ? 0.45 : 0.2 });
      });

      polygonsRef.current.push(polygon);
    }

    // Fit map to the union of all block bounds the first time we draw them
    // for a new county. Without this the user can't see anything if their
    // current zoom doesn't overlap.
    const union = new google.maps.LatLngBounds();
    let any = false;
    for (const block of blocksWithPolygon) {
      if (block.bounds) {
        union.extend({ lat: block.bounds.north, lng: block.bounds.east });
        union.extend({ lat: block.bounds.south, lng: block.bounds.west });
        any = true;
      }
    }
    if (any) mapRef.current.fitBounds(union, 24);
  }, [censusBlocks, selectedBlocks, mapLoaded, toggleBlock]);

  // Toggle block selection
  const toggleBlock = useCallback((blockId: string) => {
    setSelectedBlocks((prev) =>
      prev.includes(blockId)
        ? prev.filter((id) => id !== blockId)
        : [...prev, blockId]
    );
  }, []);

  // Select all blocks
  const selectAllBlocks = useCallback(() => {
    setSelectedBlocks(censusBlocks.map(b => b.id));
  }, [censusBlocks]);

  // Clear all selections
  const clearSelection = useCallback(() => {
    setSelectedBlocks([]);
  }, []);

  // Fetch real addresses for selected blocks
  const handleFetchAddresses = useCallback(async () => {
    if (selectedBlocks.length === 0) return;

    setIsLoading(true);
    setError(null);
    setLoadingMessage('Preparing to fetch addresses...');

    try {
      const allAddresses: Array<{
        street: string;
        unit?: string;
        city: string;
        state: string;
        zipCode: string;
        location: { lat: number; lng: number };
        selected: boolean;
      }> = [];

      // Process each selected block
      for (let i = 0; i < selectedBlocks.length; i++) {
        const blockId = selectedBlocks[i];
        const block = censusBlocks.find(b => b.id === blockId);

        if (!block) continue;

        setLoadingMessage(`Fetching addresses for ${block.name} (${i + 1}/${selectedBlocks.length})...`);

        // If we have a polygon, use it to fetch addresses
        if (block.polygon && block.polygon.length > 0) {
          try {
            const result = await addressDatabaseService.getAddressesInPolygon(
              block.polygon,
              (msg) => setLoadingMessage(`${block.name}: ${msg}`)
            );

            // Convert to workload format
            for (const addr of result.addresses) {
              allAddresses.push({
                street: `${addr.houseNumber} ${addr.street}`.trim(),
                unit: addr.unit,
                city: addr.city || '',
                state: addr.state || selectedStateAbbr,
                zipCode: addr.postcode || '',
                location: { lat: addr.lat, lng: addr.lng },
                selected: true,
              });
            }
          } catch (err) {
            console.warn(`Failed to fetch addresses for block ${block.id}:`, err);
          }
        } else if (block.bounds) {
          // Use bounds if polygon not available
          try {
            const result = await addressDatabaseService.getAddressesInBounds(
              block.bounds,
              (msg) => setLoadingMessage(`${block.name}: ${msg}`)
            );

            for (const addr of result.addresses) {
              allAddresses.push({
                street: `${addr.houseNumber} ${addr.street}`.trim(),
                unit: addr.unit,
                city: addr.city || '',
                state: addr.state || selectedStateAbbr,
                zipCode: addr.postcode || '',
                location: { lat: addr.lat, lng: addr.lng },
                selected: true,
              });
            }
          } catch (err) {
            console.warn(`Failed to fetch addresses for block ${block.id}:`, err);
          }
        }

        // Small delay between blocks to avoid rate limiting
        if (i < selectedBlocks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      if (allAddresses.length === 0) {
        setError('No addresses found in the selected blocks. Try selecting different blocks or use the polygon drawing tool for more accurate results.');
      } else {
        setLoadingMessage(`Found ${allAddresses.length} addresses!`);
        setCreationAddresses(allAddresses);
      }
    } catch (err: any) {
      console.error('Error fetching addresses:', err);
      setError(err.message || 'Failed to fetch addresses');
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  }, [selectedBlocks, censusBlocks, selectedStateAbbr, setCreationAddresses]);

  // Calculate estimated households for selected blocks
  const estimatedHouseholds = censusBlocks
    .filter(b => selectedBlocks.includes(b.id))
    .reduce((sum, b) => sum + Math.round(b.population / 2.5), 0); // ~2.5 people per household

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Select Census Blocks
        </h2>
        <p className="text-gray-600">
          Choose specific census block groups to create walklists for every home in those areas.
        </p>
      </div>

      {/* State and County Selection */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            State
          </label>
          <select
            value={selectedState}
            onChange={(e) => handleStateChange(e.target.value)}
            disabled={isLoading}
            className="w-full rounded-md border border-gray-300 px-3 py-2 disabled:bg-gray-100"
          >
            <option value="">-- Select State --</option>
            {US_STATES.map((state) => (
              <option key={state.code} value={state.code}>
                {state.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            County
          </label>
          <select
            value={selectedCounty}
            onChange={(e) => handleCountyChange(e.target.value)}
            disabled={!selectedState || counties.length === 0 || isLoading}
            className="w-full rounded-md border border-gray-300 px-3 py-2 disabled:bg-gray-100"
          >
            <option value="">-- Select County --</option>
            {counties.map((county) => (
              <option key={county.fipsCode} value={county.fipsCode}>
                {county.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Loading indicator */}
      {isLoading && (
        <div className="mb-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
          <div className="flex items-center">
            <Loader2 className="h-5 w-5 text-indigo-600 animate-spin mr-3" />
            <span className="text-indigo-700">{loadingMessage || 'Loading...'}</span>
          </div>
        </div>
      )}

      {/* Census Block Groups List */}
      {censusBlocks.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900">
              Census Block Groups ({censusBlocks.length} found)
            </h3>
            <div className="flex gap-2">
              <button
                onClick={selectAllBlocks}
                disabled={isLoading}
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                Select All
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={clearSelection}
                disabled={isLoading}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Clear
              </button>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-64 overflow-y-auto">
            {censusBlocks.map((block) => (
              <label
                key={block.id}
                className={`flex items-center p-4 cursor-pointer hover:bg-gray-50 ${
                  selectedBlocks.includes(block.id) ? 'bg-indigo-50' : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedBlocks.includes(block.id)}
                  onChange={() => toggleBlock(block.id)}
                  disabled={isLoading}
                  className="h-4 w-4 text-indigo-600 rounded border-gray-300"
                />
                <div className="ml-3 flex-1">
                  <p className="font-medium text-gray-900 text-sm">{block.name}</p>
                  <p className="text-xs text-gray-500">
                    Tract {block.tractCode} • Block Group {block.blockGroupCode}
                    {block.polygon && (
                      <span className="ml-2 text-green-600">
                        <MapPin className="inline h-3 w-3" /> Boundaries loaded
                      </span>
                    )}
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-gray-700">
                    {block.population.toLocaleString()}
                  </span>
                  <p className="text-xs text-gray-400">population</p>
                </div>
              </label>
            ))}
          </div>

          {/* Selection summary and fetch button */}
          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {selectedBlocks.length} block{selectedBlocks.length !== 1 ? 's' : ''} selected
              {selectedBlocks.length > 0 && (
                <span className="text-gray-400">
                  {' '} • ~{estimatedHouseholds.toLocaleString()} households
                </span>
              )}
            </span>
            <button
              onClick={handleFetchAddresses}
              disabled={selectedBlocks.length === 0 || isLoading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Fetching...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Fetch Addresses
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Map Preview */}
      {selectedState && (
        <div className="rounded-lg overflow-hidden border border-gray-200 mb-4">
          <GoogleMapProvider>
            <BaseMap
              center={mapCenter}
              zoom={mapZoom}
              height={350}
              onMapLoad={(map) => {
                mapRef.current = map;
                setMapLoaded(true);
              }}
            >
              {/* Polygons are drawn via useEffect */}
            </BaseMap>
          </GoogleMapProvider>
          {censusBlocks.some(b => b.polygon) && (
            <div className="bg-gray-50 px-3 py-2 text-xs text-gray-600 flex items-center gap-4">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-gray-400 opacity-40 inline-block"></span>
                Unselected
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-indigo-600 opacity-50 inline-block"></span>
                Selected
              </span>
              <span className="text-gray-400 ml-auto">Click blocks on map to toggle selection</span>
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
          <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* TIGERweb boundaries error */}
      {boundariesError && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start">
          <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
          <p className="text-yellow-800 text-sm">{boundariesError}</p>
        </div>
      )}

      {/* Results */}
      {creation.addresses.length > 0 && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <p className="text-green-800 font-medium">
                  Found {creation.addresses.length.toLocaleString()} addresses in selected blocks
                </p>
                <p className="text-green-700 text-sm mt-1">
                  Proceed to the next step to review and create your walklist.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => exportCreationAddressesToCSV(creation.addresses, 'census_block')}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-green-300 rounded text-green-800 hover:bg-green-100"
            >
              <Download className="w-4 h-4" />
              CSV
            </button>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Grid3X3 className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-blue-800">About Census Block Groups</h4>
            <p className="text-sm text-blue-700 mt-1">
              Census block groups are geographic areas containing 600-3,000 people, used by the U.S. Census Bureau.
              Addresses are fetched from OpenStreetMap data for each selected block group. For areas with limited
              OSM coverage, the polygon drawing tool may provide better results.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CensusBlockSelector;
